/* 
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useGetBatchServPayments = (batchNo: string) => {
  const [servPayments, setServPayments] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { token } = useAuthContext();

  const { reset } = useAbortController();

  const getBatchServPayment = useCallback(async () => {
    // Judith Espinal - 04/20/2026 - SQC-557 Issue 1: without token/batchNo, finish loading and clear data (avoids stuck loadingBatch=true and stale rows after Reset).
    if (!token || !batchNo) {
      setServPayments([]);
      setErrorMessage('');
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = process.env.REACT_APP_BATCH_SERV_PAYMENTS.replace('?', batchNo);

    const controller = reset();
    const config = { signal: controller.signal };

    const formData = {
      batchNo: batchNo,
      active: true,
    };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      let response = await axiosCall({ url: url, method: 'get', data: formData }, config, headers);
      setServPayments(response);
      setLoading(false);
    } catch (error) {
      setErrorMessage('Error Loading Batch. Check Batch Number');
      setLoading(false);
      console.error(error.message);
    }
  }, [batchNo, reset, token]);

  useEffect(() => {
    getBatchServPayment();
  }, [getBatchServPayment]);

  return {
    payments: servPayments,
    batchErrorMessages: errorMessage,
    loadingBatch: loading,
  };
};

export default useGetBatchServPayments;
